import { useQueryClient } from '@tanstack/react-query';
import {
  Link,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useRouterState,
  useSearch,
} from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  Bookmark,
  Calendar,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Clock3,
  Columns3,
  FileText,
  Folder as FolderIcon,
  Inbox,
  Keyboard,
  Loader2,
  ListTodo,
  Mail,
  Menu,
  Moon,
  Pencil,
  RefreshCw,
  Search,
  Send,
  Settings,
  ShieldAlert,
  SlidersHorizontal,
  Star,
  Sun,
  TriangleAlert,
  Trash2,
  UsersRound,
  X,
} from 'lucide-react';
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import type { Folder, SpecialUse } from '@email/shared';
import { toast } from 'sonner';
import { useCreateDraft, useDrafts } from '../api/compose.js';
import { useMailRecoverySettings } from '../api/mail-recovery.js';
import { usePlannerReminders } from '../api/planner.js';
import { useNotificationState } from '../api/notifications.js';
import {
  useAccounts,
  useAuthState,
  useFolders,
  useLogout,
  useMailNavigationCounts,
  useRefreshMail,
  useSyncStatus,
} from '../api/queries.js';
import { startSse } from '../api/sse.js';
import { useMailActionRunner } from '../features/mail-actions.js';
import {
  endMailDrag,
  mailDropDecision,
  readMailDrag,
  useMailDragStore,
  type MailDropDecision,
} from '../features/mail-drag.js';
import {
  effectiveFolderPlacement,
  folderDisplayName,
  sortAccountFolders,
  sortByPreferredIds,
} from '../features/navigation/layout.js';
import { usePreferenceSync } from '../features/preferences/usePreferenceSync.js';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts.js';
import { InstallAppButton, PwaStatusBanners } from '../pwa/PwaExperience.js';
import { setWintermuteBadge } from '../pwa/notifications.js';
import { useSyncStore } from '../stores/sync.js';
import { useUiStore } from '../stores/ui.js';

const ComposeDock = lazy(() =>
  import('../features/compose/ComposeDock.js').then((module) => ({
    default: module.ComposeDock,
  })),
);
const MailViewSettings = lazy(() =>
  import('../features/mail-view/MailViewSettings.js').then((module) => ({
    default: module.MailViewSettings,
  })),
);
const NavigationSettings = lazy(() =>
  import('../features/navigation/NavigationSettings.js').then((module) => ({
    default: module.NavigationSettings,
  })),
);
const SavedSearchesDialog = lazy(() =>
  import('../features/saved-searches/SavedSearchesDialog.js').then((module) => ({
    default: module.SavedSearchesDialog,
  })),
);
const ShortcutHelp = lazy(() =>
  import('../features/shortcuts/ShortcutHelp.js').then((module) => ({
    default: module.ShortcutHelp,
  })),
);

export function AppShell() {
  const auth = useAuthState();
  const qc = useQueryClient();
  const navCollapsed = useUiStore((s) => s.navCollapsed);
  const toggleNav = useUiStore((s) => s.toggleNav);
  const defaultComposeAccountId = useUiStore((s) => s.defaultComposeAccountId);
  const navigate = useNavigate();
  const action = useMailActionRunner();
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [mailViewSettingsOpen, setMailViewSettingsOpen] = useState(false);
  const shellLocation = useRouterState({
    select: (state) => state.location,
  });
  const workspace: Workspace =
    shellLocation.pathname.startsWith('/calendar')
      ? 'calendar'
      : shellLocation.pathname.startsWith('/contacts')
        ? 'contacts'
      : shellLocation.pathname.startsWith('/planner')
          ? 'planner'
          : shellLocation.pathname.startsWith('/help')
            ? 'help'
            : shellLocation.pathname.startsWith('/settings')
              ? 'settings'
              : 'mail';
  const mailWorkspace = workspace === 'mail';
  const lastMailLocation = useRef('/mail/unified');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const showMailDisplaySettings = () => {
    setMobileNavOpen(false);
    setMailViewSettingsOpen(true);
  };

  const authenticated = auth.data?.authenticated ?? false;
  const accounts = useAccounts(authenticated);
  const drafts = useDrafts(authenticated);
  const createDraft = useCreateDraft();
  const openCompose = () => {
    if (createDraft.isPending) return;
    const account =
      accounts.data?.find(
        (candidate) => candidate.enabled && candidate.id === defaultComposeAccountId,
      ) ?? accounts.data?.find((candidate) => candidate.enabled);
    if (!account) {
      toast.error('Add and enable a mail account before composing.');
      return;
    }
    createDraft.mutate(
      { accountId: account.id },
      {
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Could not create draft.'),
      },
    );
  };
  usePreferenceSync(auth.data?.user?.id ?? null);
  useTaskReminderAlerts(auth.data?.user?.id ?? null);
  useAppIconBadge(auth.data?.user?.id ?? null);
  useEffect(() => {
    if (!authenticated) return;
    return startSse(qc);
  }, [authenticated, qc]);
  useEffect(() => {
    if (shellLocation.pathname.startsWith('/mail/')) {
      lastMailLocation.current = shellLocation.href;
    }
  }, [shellLocation.href, shellLocation.pathname]);
  useEffect(() => setMobileNavOpen(false), [shellLocation.href]);

  const toggleNavigation = () => {
    if (window.matchMedia('(max-width: 680px)').matches) {
      setMobileNavOpen((open) => !open);
    } else {
      toggleNav();
    }
  };

  useKeyboardShortcuts(
    {
      '/': (event) => {
        if (shortcutHelpOpen) return;
        event.preventDefault();
        const input = document.getElementById('mail-search-input') as HTMLInputElement | null;
        input?.focus();
        input?.select();
      },
      'g i': (event) => {
        if (shortcutHelpOpen) return;
        event.preventDefault();
        void navigate({ to: '/mail/unified' });
      },
      z: (event) => {
        if (shortcutHelpOpen) return;
        event.preventDefault();
        void action.undoLast();
      },
      c: (event) => {
        if (shortcutHelpOpen) return;
        event.preventDefault();
        openCompose();
      },
      '[Shift]+?': (event) => {
        event.preventDefault();
        setShortcutHelpOpen(true);
      },
    },
    { enabled: authenticated && mailWorkspace },
  );

  if (auth.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-ink-muted" />
      </div>
    );
  }
  if (auth.isError) return <Navigate to="/offline" />;
  if (!authenticated) return <Navigate to="/login" />;

  return (
    <div className="app-shell flex h-full flex-col">
      <TopBar
        workspace={workspace}
        mailDestination={lastMailLocation.current}
        onToggleNavigation={toggleNavigation}
        onShowShortcuts={() => setShortcutHelpOpen(true)}
        onShowMailDisplay={showMailDisplaySettings}
      />
      <PwaStatusBanners />
      <div className="app-workspace relative flex min-h-0 flex-1">
        {mailWorkspace && !navCollapsed && (
          <div className="desktop-mail-nav">
            <SideNav
              onCompose={openCompose}
              composePending={createDraft.isPending}
              onShowMailDisplay={showMailDisplaySettings}
            />
          </div>
        )}
        {mailWorkspace && mobileNavOpen ? (
          <div className="mobile-mail-nav">
            <button
              type="button"
              className="mobile-nav-backdrop"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close mail navigation"
            />
            <div className="mobile-side-panel">
              <SideNav
                onCompose={openCompose}
                composePending={createDraft.isPending}
                onShowMailDisplay={showMailDisplaySettings}
              />
            </div>
          </div>
        ) : null}
        <main className="app-main min-h-0 min-w-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      <MobileWorkspaceNav workspace={workspace} mailDestination={lastMailLocation.current} />
      <div hidden={!mailWorkspace}>
        {(drafts.data?.length ?? 0) > 0 ? (
          <Suspense fallback={<ComposeLoading />}>
            <ComposeDock drafts={drafts.data ?? []} accounts={accounts.data ?? []} />
          </Suspense>
        ) : null}
      </div>
      {shortcutHelpOpen ? (
        <Suspense fallback={<DialogLoading label="Loading shortcuts" />}>
          <ShortcutHelp onClose={() => setShortcutHelpOpen(false)} />
        </Suspense>
      ) : null}
      {mailViewSettingsOpen ? (
        <Suspense fallback={<DialogLoading label="Loading mail display settings" />}>
          <MailViewSettings onClose={() => setMailViewSettingsOpen(false)} />
        </Suspense>
      ) : null}
    </div>
  );
}

function useTaskReminderAlerts(userId: number | null): void {
  const enabled = userId !== null;
  const reminders = usePlannerReminders(enabled);
  const navigate = useNavigate();
  const shown = useRef(new Set<string>());
  useEffect(() => {
    const activeKeys = new Set<string>();
    for (const task of reminders.data?.reminders ?? []) {
      const effectiveAt = task.reminderSnoozedUntil ?? task.reminderAt ?? 0;
      const key = `${task.id}:${effectiveAt}`;
      const toastId = `task-reminder:${key}`;
      activeKeys.add(key);
      if (shown.current.has(key)) continue;
      shown.current.add(key);
      toast.warning(`Task reminder: ${task.title}`, {
        id: toastId,
        duration: Number.POSITIVE_INFINITY,
        description:
          task.dueAt === null
            ? 'Open Planner to acknowledge, snooze, or dismiss it.'
            : `Due ${new Intl.DateTimeFormat(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(task.dueAt)}`,
        action: {
          label: 'Open',
          onClick: () =>
            void navigate({
              to: '/planner',
              search: { tab: 'tasks', taskId: task.id },
            }),
        },
      });
    }
    for (const key of shown.current) {
      if (!activeKeys.has(key)) {
        toast.dismiss(`task-reminder:${key}`);
        shown.current.delete(key);
      }
    }
  }, [navigate, reminders.data?.reminders]);
}

function useAppIconBadge(userId: number | null): void {
  const enabled = userId !== null;
  const counts = useMailNavigationCounts(enabled);
  const reminders = usePlannerReminders(enabled);
  const notifications = useNotificationState(enabled);
  const badgeEnabled = notifications.data?.preferences.badgeEnabled ?? false;
  const count =
    (counts.data?.unifiedUnread ?? 0) +
    (reminders.data?.reminders.length ?? 0);
  useEffect(() => {
    void setWintermuteBadge(badgeEnabled ? count : null);
  }, [badgeEnabled, count]);
}

function TopBar({
  workspace,
  mailDestination,
  onToggleNavigation,
  onShowShortcuts,
  onShowMailDisplay,
}: {
  workspace: Workspace;
  mailDestination: string;
  onToggleNavigation: () => void;
  onShowShortcuts: () => void;
  onShowMailDisplay: () => void;
}) {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const navigate = useNavigate();
  const routeSearch = useSearch({ strict: false }) as Record<string, unknown>;
  const activeQuery = typeof routeSearch.q === 'string' ? routeSearch.q : '';
  const [query, setQuery] = useState(activeQuery);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setQuery(activeQuery), [activeQuery]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = query.trim();
    if (q) void navigate({ to: '/mail/search', search: { q, sort: 'newest' } });
    else void navigate({ to: '/mail/unified' });
  };

  const clearSearch = () => {
    setQuery('');
    if (activeQuery) void navigate({ to: '/mail/unified' });
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  const mailWorkspace = workspace === 'mail';
  const workspaceHome =
    workspace === 'calendar'
      ? '/calendar'
      : workspace === 'contacts'
        ? '/contacts'
        : workspace === 'planner'
          ? '/planner'
          : workspace === 'help'
            ? '/help'
            : workspace === 'settings'
              ? '/settings/accounts'
              : '/mail/unified';

  return (
    <header className="app-topbar flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-3">
      {mailWorkspace ? (
        <button
          onClick={onToggleNavigation}
          className="rounded-full p-2 text-ink-muted hover:bg-hover"
          title="Toggle navigation"
        >
          <Menu className="size-5" />
        </button>
      ) : null}
      <Link
        to={workspaceHome}
        className="flex items-center gap-2 px-1"
        title={`Wintermute ${workspace[0]!.toUpperCase()}${workspace.slice(1)} home`}
      >
        {workspace === 'contacts' ? (
          <UsersRound className="size-6 text-accent" />
        ) : workspace === 'calendar' ? (
          <Calendar className="size-6 text-accent" />
        ) : workspace === 'planner' ? (
          <ListTodo className="size-6 text-accent" />
        ) : workspace === 'help' ? (
          <CircleHelp className="size-6 text-accent" />
        ) : workspace === 'settings' ? (
          <Settings className="size-6 text-accent" />
        ) : (
          <Mail className="size-6 text-accent" />
        )}
        <span className="app-brand-label text-xl font-medium tracking-tight">Wintermute</span>
      </Link>
      {mailWorkspace ? (
        <div className="app-search mx-auto w-full max-w-2xl px-4">
          <form onSubmit={submitSearch} className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ink-muted" />
            <input
              id="mail-search-input"
              ref={searchInputRef}
              type="text"
              role="searchbox"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Escape' || !query) return;
                event.preventDefault();
                event.stopPropagation();
                clearSearch();
              }}
              placeholder="Search mail"
              aria-label="Search mail"
              title="Exact words are ANDed. Use quotes for a phrase, OR for alternatives, or a trailing * for a prefix."
              className="w-full rounded-full bg-inset py-2.5 pl-11 pr-11 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30"
            />
            {query ? (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-2 text-ink-muted hover:bg-hover hover:text-ink"
                aria-label="Clear mail search"
                title="Clear search (Escape)"
              >
                <X className="size-4" />
              </button>
            ) : null}
          </form>
        </div>
      ) : (
        <div className="min-w-0 flex-1" />
      )}
      <WorkspaceSwitcher
        workspace={workspace}
        mailDestination={mailDestination}
      />
      {mailWorkspace ? (
        <>
          <MailRefreshButton />
          <SyncIndicator />
          <button
            type="button"
            onClick={onShowShortcuts}
            className="topbar-optional rounded-full p-2 text-ink-muted hover:bg-hover"
            title="Keyboard shortcuts (?)"
            aria-label="Show keyboard shortcuts"
          >
            <Keyboard className="size-5" />
          </button>
          <button
            type="button"
            onClick={onShowMailDisplay}
            className="topbar-mobile-secondary rounded-full p-2 text-ink-muted hover:bg-hover"
            title="Mail display and reading layout"
            aria-label="Customize mail display"
          >
            <Columns3 className="size-5" />
          </button>
        </>
      ) : null}
      <InstallAppButton />
      <Link
        to="/help"
        search={{}}
        className="topbar-help-link rounded-full p-2 text-ink-muted hover:bg-hover hover:text-ink"
        activeProps={{ className: 'bg-accent-soft text-accent' }}
        title="Help Centre"
        aria-label="Open Help Centre"
      >
        <CircleHelp className="size-5" />
      </Link>
      <button
        onClick={() => setTheme(nextTheme)}
        className="topbar-optional rounded-full p-2 text-ink-muted hover:bg-hover"
        title={`Switch to ${nextTheme} theme`}
      >
        {theme === 'dark' ? <Sun className="size-5" /> : <Moon className="size-5" />}
      </button>
      <Link
        to="/settings/accounts"
        className="topbar-settings-link rounded-full p-2 text-ink-muted hover:bg-hover"
        title="Settings"
      >
        <Settings className="size-5" />
      </Link>
      <UserMenu />
    </header>
  );
}

function WorkspaceSwitcher({
  workspace,
  mailDestination,
}: {
  workspace: Workspace;
  mailDestination: string;
}) {
  const navigate = useNavigate();
  const itemClass =
    'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors';
  return (
    <nav
      className="desktop-workspace-switcher flex shrink-0 items-center rounded-full bg-inset p-0.5"
      aria-label="Switch workspace"
    >
      <button
        type="button"
        onClick={() => void navigate({ href: mailDestination })}
        className={`${itemClass} ${
          workspace === 'mail'
            ? 'bg-surface text-accent shadow-sm'
            : 'text-ink-muted hover:text-ink'
        }`}
        aria-current={workspace === 'mail' ? 'page' : undefined}
        title="Open Mail"
      >
        <Mail className="size-4" />
        <span className="workspace-switch-label">Mail</span>
      </button>
      <button
        type="button"
        onClick={() => void navigate({ to: '/calendar' })}
        className={`${itemClass} ${
          workspace === 'calendar'
            ? 'bg-surface text-accent shadow-sm'
            : 'text-ink-muted hover:text-ink'
        }`}
        aria-current={workspace === 'calendar' ? 'page' : undefined}
        title="Open Calendar"
      >
        <Calendar className="size-4" />
        <span className="workspace-switch-label">Calendar</span>
      </button>
      <button
        type="button"
        onClick={() => void navigate({ to: '/contacts' })}
        className={`${itemClass} ${
          workspace === 'contacts'
            ? 'bg-surface text-accent shadow-sm'
            : 'text-ink-muted hover:text-ink'
        }`}
        aria-current={workspace === 'contacts' ? 'page' : undefined}
        title="Open Contacts"
      >
        <UsersRound className="size-4" />
        <span className="workspace-switch-label">Contacts</span>
      </button>
      <button
        type="button"
        onClick={() => void navigate({ to: '/planner', search: {} })}
        className={`${itemClass} ${
          workspace === 'planner'
            ? 'bg-surface text-accent shadow-sm'
            : 'text-ink-muted hover:text-ink'
        }`}
        aria-current={workspace === 'planner' ? 'page' : undefined}
        title="Open Tasks and Notes"
      >
        <ListTodo className="size-4" />
        <span className="workspace-switch-label">Tasks</span>
      </button>
    </nav>
  );
}

function MobileWorkspaceNav({
  workspace,
  mailDestination,
}: {
  workspace: Workspace;
  mailDestination: string;
}) {
  const navigate = useNavigate();
  const items: Array<{
    workspace: Workspace;
    label: string;
    icon: ReactNode;
    open: () => void;
  }> = [
    {
      workspace: 'mail',
      label: 'Mail',
      icon: <Mail className="size-5" />,
      open: () => void navigate({ href: mailDestination }),
    },
    {
      workspace: 'calendar',
      label: 'Calendar',
      icon: <Calendar className="size-5" />,
      open: () => void navigate({ to: '/calendar' }),
    },
    {
      workspace: 'contacts',
      label: 'Contacts',
      icon: <UsersRound className="size-5" />,
      open: () => void navigate({ to: '/contacts' }),
    },
    {
      workspace: 'planner',
      label: 'Tasks',
      icon: <ListTodo className="size-5" />,
      open: () => void navigate({ to: '/planner', search: {} }),
    },
    {
      workspace: 'settings',
      label: 'Settings',
      icon: <Settings className="size-5" />,
      open: () => void navigate({ to: '/settings/accounts' }),
    },
  ];

  return (
    <nav className="mobile-workspace-nav" aria-label="Switch workspace">
      {items.map((item) => {
        const active = workspace === item.workspace;
        return (
          <button
            key={item.workspace}
            type="button"
            onClick={item.open}
            className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[0.68rem] font-medium ${
              active ? 'text-accent' : 'text-ink-muted'
            }`}
            aria-current={active ? 'page' : undefined}
          >
            {item.icon}
            <span className="truncate">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

type Workspace = 'mail' | 'calendar' | 'contacts' | 'planner' | 'help' | 'settings';

function MailRefreshButton() {
  const { data: accounts } = useAccounts();
  const params = useParams({ strict: false });
  const refresh = useRefreshMail();
  const rawAccountId = params.accountId;
  const scopedAccountId =
    typeof rawAccountId === 'string' && /^\d+$/.test(rawAccountId)
      ? Number(rawAccountId)
      : undefined;
  const enabledAccounts = (accounts ?? []).filter((account) => account.enabled);
  const scopedAccount =
    scopedAccountId === undefined
      ? undefined
      : enabledAccounts.find((account) => account.id === scopedAccountId);
  const targets =
    scopedAccountId === undefined ? enabledAccounts : scopedAccount ? [scopedAccount] : [];
  const syncing = targets.some((account) => account.status === 'syncing');
  const pending = refresh.isPending || syncing;
  const scopeLabel = scopedAccount ? scopedAccount.name : 'all accounts';

  return (
    <button
      type="button"
      onClick={() =>
        refresh.mutate(scopedAccountId, {
          onSuccess: (result) => {
            if (result.accountIds.length === 0) {
              toast.info('There are no enabled mail accounts to refresh.');
              return;
            }
            toast.success(
              result.accountIds.length === 1
                ? `Checking ${scopedAccount?.name ?? 'mail'} for new messages`
                : `Checking ${result.accountIds.length.toLocaleString()} accounts for new messages`,
            );
          },
          onError: (error) =>
            toast.error(error instanceof Error ? error.message : 'Could not refresh mail.'),
        })
      }
      disabled={targets.length === 0 || pending}
      className="rounded-full p-2 text-ink-muted hover:bg-hover hover:text-ink disabled:opacity-40"
      title={pending ? `Checking ${scopeLabel} for new mail…` : `Check ${scopeLabel} for new mail`}
      aria-label={pending ? `Checking ${scopeLabel} for new mail` : `Check ${scopeLabel} for new mail`}
    >
      <RefreshCw className={`size-5 ${pending ? 'animate-spin' : ''}`} />
    </button>
  );
}

function UserMenu() {
  const { data: auth } = useAuthState();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const user = auth?.user;
  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex size-9 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold hover:opacity-90"
        title={user.username}
      >
        {user.username.charAt(0).toUpperCase()}
      </button>
      {open ? (
        <div className="absolute right-0 top-11 z-50 w-56 rounded-xl border border-line bg-surface py-2 shadow-lg">
          <div className="border-b border-line px-4 pb-2">
            <div className="font-medium">{user.displayName || user.username}</div>
            <div className="text-xs text-ink-muted">
              @{user.username}
              {user.isAdmin ? ' · admin' : ''}
            </div>
          </div>
          <Link
            to="/help"
            search={{}}
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm hover:bg-hover"
          >
            Help Centre
          </Link>
          <Link
            to="/settings/security"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm hover:bg-hover"
          >
            Security settings
          </Link>
          <button
            onClick={() => logout.mutate()}
            className="block w-full px-4 py-2 text-left text-sm hover:bg-hover"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function SyncIndicator() {
  const { data: accounts } = useAccounts();
  const { data: status } = useSyncStatus();
  const progress = useSyncStore((s) => s.byAccount);

  const syncing = (accounts ?? []).filter((a) => a.enabled && a.status === 'syncing');
  const failed =
    status?.accounts.reduce(
      (total, account) => total + account.queue.failed + account.outbox.failed,
      0,
    ) ?? 0;
  const problemAccounts = (accounts ?? []).filter(
    (account) =>
      account.enabled && (account.status === 'auth_failed' || account.status === 'error'),
  );
  const incompleteMirrors =
    status?.accounts.filter(
      (account) =>
        account.enabled &&
        account.backfillFoldersRemaining === 0 &&
        account.missingMessageCount > 0,
    ) ?? [];
  if (failed > 0 || problemAccounts.length > 0 || incompleteMirrors.length > 0) {
    const label =
      failed > 0
        ? `${failed.toLocaleString()} failed mail ${failed === 1 ? 'operation' : 'operations'}`
        : problemAccounts.length > 0
          ? `${problemAccounts.length.toLocaleString()} account ${problemAccounts.length === 1 ? 'issue' : 'issues'}`
          : `${incompleteMirrors.length.toLocaleString()} incomplete mail ${incompleteMirrors.length === 1 ? 'mirror' : 'mirrors'}`;
    const details = [
      failed > 0 ? label : null,
      ...problemAccounts.map((account) => `${account.name}: ${account.statusError ?? account.status}`),
      ...incompleteMirrors.map(
        (account) =>
          `${account.name}: ${account.missingMessageCount.toLocaleString()} retrievable ${account.missingMessageCount === 1 ? 'message is' : 'messages are'} still missing`,
      ),
    ].filter(Boolean);
    return (
      <Link
        to="/settings/activity"
        className="flex items-center gap-2 rounded-full px-2 py-2 font-medium text-danger hover:bg-danger/10"
        title={details.join('\n')}
      >
        <TriangleAlert className="size-4" />
        <span className="hidden text-xs md:block">{label}</span>
      </Link>
    );
  }

  const queued = status?.accounts.reduce(
    (total, account) =>
      total + account.queue.pending + account.queue.inflight + account.outbox.sentCopyPending,
    0,
  ) ?? 0;
  if (syncing.length === 0 && queued === 0) return null;

  const lines = syncing.map((a) => {
    const p = progress[a.id];
    if (p && p.total > 0 && p.phase !== 'idle') {
      return `${a.name}: ${p.done.toLocaleString()} of ${p.total.toLocaleString()} (${p.phase})`;
    }
    return `${a.name}: syncing…`;
  });

  return (
    <Link
      to="/settings/activity"
      className="flex items-center gap-2 rounded-full px-2 py-2 text-ink-muted"
      title={[...lines, queued > 0 ? `${queued.toLocaleString()} mail actions waiting` : null].filter(Boolean).join('\n')}
    >
      <span
        className="size-2 rounded-full bg-accent animate-pulse"
        aria-hidden="true"
      />
      <span className="hidden text-xs md:block">{syncing.length > 0 ? 'Syncing' : 'Sending changes'}</span>
    </Link>
  );
}

function folderIcon(specialUse: SpecialUse | null): ReactNode {
  const cls = 'size-4 shrink-0';
  switch (specialUse) {
    case 'inbox':
      return <Inbox className={cls} />;
    case 'sent':
      return <Send className={cls} />;
    case 'drafts':
      return <FileText className={cls} />;
    case 'trash':
      return <Trash2 className={cls} />;
    case 'junk':
      return <ShieldAlert className={cls} />;
    case 'archive':
    case 'all':
      return <Archive className={cls} />;
    case 'flagged':
      return <Star className={cls} />;
    default:
      return <FolderIcon className={cls} />;
  }
}

function SideNav({
  onCompose,
  composePending,
  onShowMailDisplay,
}: {
  onCompose: () => void;
  composePending: boolean;
  onShowMailDisplay: () => void;
}) {
  const { data: accounts } = useAccounts();
  const { data: folders } = useFolders();
  const { data: navigationCounts } = useMailNavigationCounts();
  const { data: recoverySettings } = useMailRecoverySettings();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savedSearchesOpen, setSavedSearchesOpen] = useState(false);
  const savedMailSearches = useUiStore((state) => state.savedMailSearches);
  const collapsedAccountIds = useUiStore((state) => state.collapsedAccountIds);
  const expandedMoreAccountIds = useUiStore((state) => state.expandedMoreAccountIds);
  const accountOrder = useUiStore((state) => state.accountOrder);
  const folderOrderByAccount = useUiStore((state) => state.folderOrderByAccount);
  const folderPlacementById = useUiStore((state) => state.folderPlacementById);
  const toggleAccountCollapsed = useUiStore((state) => state.toggleAccountCollapsed);
  const toggleAccountMore = useUiStore((state) => state.toggleAccountMore);
  const dragPayload = useMailDragStore((state) => state.payload);
  const mailAction = useMailActionRunner();

  const unifiedUnread = navigationCounts?.unifiedUnread ?? 0;
  const accountUnreadById = new Map(
    navigationCounts?.accounts.map((count) => [count.id, count.unread]) ?? [],
  );
  const folderUnreadById = new Map(
    navigationCounts?.folders.map((count) => [count.id, count.unread]) ?? [],
  );
  const orderedAccounts = sortByPreferredIds(accounts ?? [], accountOrder);
  const pinnedSearches = savedMailSearches.filter((search) => search.pinned);

  const dropMail = (event: DragEvent<HTMLAnchorElement>, folder: Folder) => {
    event.preventDefault();
    event.stopPropagation();
    const payload = readMailDrag(event.dataTransfer) ?? dragPayload;
    endMailDrag();
    if (!payload) return;
    const decision = mailDropDecision(payload, folder);
    if (decision === 'account_mismatch') {
      toast.error('Mail can only be moved to folders in the same account.');
      return;
    }
    if (decision === 'same_folder') {
      toast.info(`This mail is already in ${folderDisplayName(folder)}.`);
      return;
    }
    if (decision === 'virtual_folder') {
      toast.error(`${folderDisplayName(folder)} is a view, not a destination folder.`);
      return;
    }
    if (mailAction.isPending) {
      toast.info('Wait for the current mail action to finish, then drop again.');
      return;
    }
    const count = payload.threadIds.length;
    void mailAction
      .run(
        {
          threadIds: payload.threadIds,
          action: 'move',
          targetFolderId: folder.id,
          ...(payload.sourceFolderId === undefined
            ? {}
            : { sourceFolderId: payload.sourceFolderId }),
        },
        count === 1
          ? `Moved conversation to ${folderDisplayName(folder)}`
          : `Moved ${count.toLocaleString()} conversations to ${folderDisplayName(folder)}`,
      )
      .then((result) => {
        if (result.affectedMessageIds.length === 0) {
          toast.info(`No messages needed moving to ${folderDisplayName(folder)}.`);
        }
      })
      .catch(() => undefined);
  };

  const mailDropFor = (folder: Folder) =>
    dragPayload
      ? {
          decision: mailDropDecision(dragPayload, folder),
          onDrop: (event: DragEvent<HTMLAnchorElement>) => dropMail(event, folder),
        }
      : undefined;

  return (
    <nav className="app-side-nav flex w-64 shrink-0 flex-col overflow-y-auto border-r border-line px-2 py-3">
      <button
        type="button"
        onClick={onCompose}
        disabled={composePending || !(accounts ?? []).some((account) => account.enabled)}
        title="Compose (c)"
        className="mx-2 mb-4 flex w-fit items-center gap-3 rounded-2xl bg-accent-soft px-5 py-3.5 text-sm font-medium hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {composePending ? <Loader2 className="size-4 animate-spin" /> : <Pencil className="size-4" />}
        Compose
      </button>

      <NavItem to="/mail/unified" icon={<Inbox className="size-4 shrink-0" />} badge={unifiedUnread}>
        Unified inbox
      </NavItem>
      <NavItem
        to="/mail/snoozed"
        icon={<Clock3 className="size-4 shrink-0" />}
        badge={navigationCounts?.snoozed ?? 0}
      >
        Snoozed
      </NavItem>
      <NavItem
        to="/mail/recovery"
        icon={<ArchiveRestore className="size-4 shrink-0" />}
        badge={recoverySettings?.recoverableMessages ?? 0}
      >
        Recently removed
      </NavItem>

      <div className="mt-3">
        <div className="flex items-center gap-2 px-3 py-1 text-xs font-semibold text-ink-muted">
          <Bookmark className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1">Saved searches</span>
          <button
            type="button"
            onClick={() => setSavedSearchesOpen(true)}
            className="rounded-full px-2 py-1 font-medium hover:bg-hover hover:text-ink"
            title="Manage saved searches"
          >
            Manage
          </button>
        </div>
        {pinnedSearches.length === 0 ? (
          <button
            type="button"
            onClick={() => setSavedSearchesOpen(true)}
            className="w-full rounded-full py-1.5 pl-10 pr-3 text-left text-xs text-ink-muted hover:bg-hover hover:text-ink"
          >
            Save a mail search
          </button>
        ) : (
          pinnedSearches.map((search) => (
            <Link
              key={search.id}
              to="/mail/search"
              search={{ q: search.query, sort: search.sort }}
              activeOptions={{ includeSearch: true }}
              className="flex items-center gap-3 rounded-full py-1.5 pl-4 pr-3 text-sm text-ink hover:bg-hover"
              activeProps={{ className: 'bg-accent-soft font-semibold' }}
              title={`${search.name}: ${search.query}`}
            >
              <Bookmark className="size-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{search.name}</span>
            </Link>
          ))
        )}
      </div>

      {orderedAccounts.map((account) => {
        const accountFolders = sortAccountFolders(
          (folders ?? []).filter((folder) => folder.accountId === account.id),
          folderOrderByAccount[String(account.id)] ?? [],
        );
        const mainFolders = accountFolders.filter(
          (folder) => effectiveFolderPlacement(folder, folderPlacementById) === 'main',
        );
        const moreFolders = accountFolders.filter(
          (folder) => effectiveFolderPlacement(folder, folderPlacementById) === 'more',
        );
        const receivingDrag = dragPayload?.accountId === account.id;
        const collapsed = receivingDrag ? false : collapsedAccountIds.includes(account.id);
        const moreExpanded = receivingDrag || expandedMoreAccountIds.includes(account.id);
        const accountUnread = accountUnreadById.get(account.id) ?? 0;
        return (
          <div key={account.id} className="mt-4">
            <button
              type="button"
              onClick={() => toggleAccountCollapsed(account.id)}
              className={`flex w-full items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-hover hover:text-ink ${receivingDrag ? 'bg-accent-soft text-accent' : ''}`}
              title={`${collapsed ? 'Expand' : 'Collapse'} ${account.name}`}
              aria-expanded={!collapsed}
            >
              {collapsed ? (
                <ChevronRight className="size-3.5 shrink-0" />
              ) : (
                <ChevronDown className="size-3.5 shrink-0" />
              )}
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: account.color }}
              />
              <span className="min-w-0 flex-1 truncate text-left" title={account.email}>
                {account.name}
              </span>
              {account.status === 'error' || account.status === 'auth_failed' ? (
                <span className="text-danger" title={account.statusError ?? 'connection error'}>
                  !
                </span>
              ) : null}
              {accountUnread > 0 ? (
                <span className="text-xs font-semibold text-ink">
                  {accountUnread.toLocaleString()}
                </span>
              ) : null}
            </button>
            {!collapsed ? (
              <>
                {mainFolders.map((folder) => (
                  <NavItem
                    key={folder.id}
                    to="/mail/a/$accountId/f/$folderId"
                    params={{ accountId: String(account.id), folderId: String(folder.id) }}
                    icon={folderIcon(folder.specialUse)}
                    badge={folderUnreadById.get(folder.id) ?? 0}
                    mailDrop={mailDropFor(folder)}
                  >
                    {folderDisplayName(folder)}
                  </NavItem>
                ))}
                {moreFolders.length > 0 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleAccountMore(account.id)}
                      className="flex w-full items-center gap-3 rounded-full py-1.5 pl-4 pr-3 text-sm text-ink-muted hover:bg-hover hover:text-ink"
                      aria-expanded={moreExpanded}
                    >
                      {moreExpanded ? (
                        <ChevronDown className="size-4 shrink-0" />
                      ) : (
                        <ChevronRight className="size-4 shrink-0" />
                      )}
                      <span className="min-w-0 flex-1 text-left">More</span>
                    </button>
                    {moreExpanded
                      ? moreFolders.map((folder) => (
                          <NavItem
                            key={folder.id}
                            to="/mail/a/$accountId/f/$folderId"
                            params={{
                              accountId: String(account.id),
                              folderId: String(folder.id),
                            }}
                            icon={folderIcon(folder.specialUse)}
                            badge={folderUnreadById.get(folder.id) ?? 0}
                            mailDrop={mailDropFor(folder)}
                          >
                            {folderDisplayName(folder)}
                          </NavItem>
                        ))
                      : null}
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        );
      })}

      <div className="mt-6 border-t border-line pt-3">
        <button
          type="button"
          onClick={onShowMailDisplay}
          className="flex w-full items-center gap-3 rounded-full py-1.5 pl-4 pr-3 text-sm text-ink hover:bg-hover"
        >
          <Columns3 className="size-4 shrink-0" />
          <span>Mail display</span>
        </button>
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          className="flex w-full items-center gap-3 rounded-full py-1.5 pl-4 pr-3 text-sm text-ink hover:bg-hover"
        >
          <SlidersHorizontal className="size-4 shrink-0" />
          <span>Customize navigation</span>
        </button>
        <NavItem to="/settings/accounts" icon={<Settings className="size-4 shrink-0" />}>
          Settings
        </NavItem>
      </div>
      {settingsOpen ? (
        <Suspense fallback={<DialogLoading label="Loading navigation settings" />}>
          <NavigationSettings
            accounts={accounts ?? []}
            folders={folders ?? []}
            unreadByFolder={folderUnreadById}
            onClose={() => setSettingsOpen(false)}
          />
        </Suspense>
      ) : null}
      {savedSearchesOpen ? (
        <Suspense fallback={<DialogLoading label="Loading saved searches" />}>
          <SavedSearchesDialog onClose={() => setSavedSearchesOpen(false)} />
        </Suspense>
      ) : null}
    </nav>
  );
}

function ComposeLoading() {
  return (
    <div
      className="pointer-events-none fixed bottom-20 right-4 z-50 flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 text-sm text-ink-muted shadow-xl md:bottom-4"
      role="status"
    >
      <Loader2 className="size-4 animate-spin" /> Opening compose…
    </div>
  );
}

function DialogLoading({ label }: { label: string }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4"
      role="status"
    >
      <span className="flex items-center gap-2 rounded-full bg-surface px-4 py-2 text-sm text-ink-muted shadow-xl">
        <Loader2 className="size-4 animate-spin" /> {label}…
      </span>
    </div>
  );
}

function NavItem(props: {
  to: string;
  params?: Record<string, string>;
  icon: ReactNode;
  badge?: number;
  children: ReactNode;
  mailDrop?:
    | {
        decision: MailDropDecision;
        onDrop: (event: DragEvent<HTMLAnchorElement>) => void;
      }
    | undefined;
}) {
  const dropActive = props.mailDrop !== undefined;
  const dropAllowed = props.mailDrop?.decision === 'allowed';
  return (
    <Link
      // Route params are validated by the router at runtime; the generic string
      // here keeps NavItem reusable across static and param routes.
      to={props.to as '/mail/unified'}
      params={props.params}
      className={`flex items-center gap-3 rounded-full py-1.5 pl-4 pr-3 text-sm text-ink hover:bg-hover ${
        dropActive
          ? dropAllowed
            ? 'bg-accent-soft outline outline-1 -outline-offset-1 outline-accent'
            : 'opacity-40'
          : ''
      }`}
      activeProps={{ className: 'bg-accent-soft font-semibold' }}
      onDragOver={(event) => {
        if (!props.mailDrop) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = dropAllowed ? 'move' : 'none';
      }}
      onDrop={(event) => props.mailDrop?.onDrop(event)}
    >
      {props.icon}
      <span className="min-w-0 flex-1 truncate">{props.children}</span>
      {props.badge !== undefined && props.badge > 0 ? (
        <span className="text-xs font-semibold">{props.badge.toLocaleString()}</span>
      ) : null}
    </Link>
  );
}
